'use client'

import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import { useState } from 'react'
import { BORDERED_INPUT_CLASS } from '@/components/Form/Input/classes'
import { usePageSearch } from '@/hooks/business/pages/usePageSearch'
import { useDebounce } from '@/hooks/shared/useDebounce'

/** Espera depois da última tecla antes de buscar. */
const SEARCH_DELAY_MS = 400

/** O trecho vem da API com entidades HTML e às vezes com tags cortadas: vira texto simples para o resumo. */
function extractPlainText(encoded: string): string {
    const decoded = encoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&#39;/g, "'")
    return decoded
        .replace(/<[^>]*>/g, ' ')
        .replace(/<[^>]*$/, '')
        .replace(/\s+/g, ' ')
        .trim()
}

/** Busca da central de ajuda: resultados linkados pelo slug (a rota /p/[slug] resolve id só para links antigos). */
const PageSearch: React.FC = () => {
    const [term, setTerm] = useState('')
    const debouncedTerm = useDebounce(term, SEARCH_DELAY_MS)
    const { results, isSearching, isError } = usePageSearch(debouncedTerm)

    const touched = term.trim().length > 0
    const loading = touched && (term !== debouncedTerm || isSearching)

    return (
        <div className="w-full">
            <input
                type="search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Pesquisar..."
                className={`w-full ${BORDERED_INPUT_CLASS}`}
            />

            {touched && (
                <div className="mt-4">
                    {loading && <p className="text-sm text-gray-500 px-1">Buscando...</p>}
                    {!loading && isError && (
                        <p className="text-sm text-gray-500 px-1">Não foi possível buscar agora. Tente novamente.</p>
                    )}
                    {!loading && !isError && results.length === 0 && (
                        <p className="text-sm text-gray-500 px-1">Nenhum resultado encontrado.</p>
                    )}
                    <ul className="flex flex-col gap-2">
                        {results.map((r) => (
                            <li key={r.id}>
                                <Link
                                    href={`/p/${r.slug}`}
                                    className="group flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-white p-4 hover:border-tertiary-green hover:shadow-md transition"
                                >
                                    <span className="min-w-0">
                                        <span className="block font-bold text-primary text-sm">{r.name}</span>
                                        {r.excerpt && (
                                            <span className="block text-xs text-gray-500 mt-1 line-clamp-2">
                                                {extractPlainText(r.excerpt)}
                                            </span>
                                        )}
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        className="text-gray-300 group-hover:text-tertiary-green mt-1 shrink-0"
                                    />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

export default PageSearch
